/**
 * @fileoverview Unified registry for all admin table entities. This is the single source
 * of truth for entity metadata, data sources, and caches. Routes, navigation, and the
 * hub page all derive from this registry.
 */

import * as React from 'react';
import { AccountCircle, Architecture, AttachFile, AutoAwesome, Business, Category as CategoryIcon, History as HistoryIcon, Inventory2, Login as LoginIcon, Note as NoteIcon, Person as PersonIcon, Restaurant, Scale, Speed, Storage, Tag as TagIcon, Visibility } from '@mui/icons-material';
import { BiArea } from 'react-icons/bi';
import { LuTag } from 'react-icons/lu';
import { MdSubdirectoryArrowRight, MdTraffic } from 'react-icons/md';
import { VscSymbolClass } from 'react-icons/vsc';
import * as api from '@/api';
import type { DataSourceCache } from '@/api';
import type { DataModel, DataSource } from '../types';

// =============================================================================
// Naming Convention Helpers
// =============================================================================

/**
 * Converts entity key to camelCase (for accessing exports).
 * @example toCamelCase('note_tag') // 'noteTag'
 */
function toCamelCase(entity: string): string {
  return entity.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

/**
 * Creates a camelCase parameter name from an entity key.
 * @example toParamName('note_tag') // 'noteTagId'
 */
export function toParamName(entity: string): string {
  return toCamelCase(entity) + 'Id';
}

// Dynamic property access - TypeScript can't infer types from computed keys
const apiLookup = api as Record<string, unknown>;

/** Gets data source for an entity by convention: {camelCase}DataSource */
function getDataSource(entity: string): DataSource<DataModel> {
  return apiLookup[`${toCamelCase(entity)}DataSource`] as DataSource<DataModel>;
}

/** Gets cache for an entity by convention: {camelCase}Cache */
function getCache(entity: string): DataSourceCache {
  return apiLookup[`${toCamelCase(entity)}Cache`] as DataSourceCache;
}

// =============================================================================
// Entity Registry (only icon and description needed per entity)
// =============================================================================

/**
 * Minimal configuration stored per entity.
 */
interface EntityRegistryEntry {
  icon: React.ReactNode;
  description: string;
  /** If true, entity is read-only (no create/edit/delete in admin) */
  readOnly?: boolean;
}

/**
 * Full entity configuration including computed fields.
 */
export interface EntityConfig {
  dataSource: DataSource<DataModel>;
  cache: DataSourceCache;
  icon: React.ReactNode;
  description: string;
  paramName: string;
  /** If true, entity is read-only (no create/edit/delete in admin) */
  readOnly?: boolean;
}

/**
 * Unified registry of all admin table entities.
 * Add new entities here to automatically register them in routes, navigation, and the hub page.
 * Data source and cache are derived from the entity key by naming convention.
 */
const ENTITY_REGISTRY_DATA: Record<string, EntityRegistryEntry> = {
  account: {
    icon: <AccountCircle />,
    description: 'User accounts with authentication provider information.',
  },
  ai_prompt: {
    icon: <AutoAwesome />,
    description: 'Reusable AI prompt templates with configurable model settings.',
  },
  area: {
    icon: <BiArea size={26} />,
    description: 'Areas of life representing ongoing commitments without a specific end date.',
  },
  attachment: {
    icon: <AttachFile />,
    description: 'Polymorphic file attachments linked to any entity.',
  },
  calorie: {
    icon: <Restaurant />,
    description: 'Calorie intake entries, summed per day on the Weight page.',
  },
  category: {
    icon: <CategoryIcon />,
    description: 'Top-level groups for organising notes into broad categories.',
  },
  embedding: {
    icon: <Storage />,
    description: 'Queue for generating AI embeddings for notes.',
  },
  history: {
    icon: <HistoryIcon />,
    description: 'Version history tracking for note changes.',
  },
  kind: {
    icon: <VscSymbolClass size={20} />,
    description: 'Taxonomy for classifying notes.',
  },
  login: {
    icon: <LoginIcon />,
    description: 'Immutable login records storing authentication attempts.',
  },
  note: {
    icon: <NoteIcon />,
    description: 'Main content storage with metadata and relationships.',
  },
  note_tag: {
    icon: <LuTag size={20} />,
    description: 'Many-to-many relationship between notes and tags.',
  },
  note_visit: {
    icon: <Visibility />,
    description: 'Event log tracking note access and visits by users.',
    readOnly: true,
  },
  organisation: {
    icon: <Business />,
    description: 'Organisations and affiliations.',
  },
  person: {
    icon: <PersonIcon />,
    description: 'People and related details. Can be linked to notes, orgs, projects, or areas.',
  },
  project: {
    icon: <Architecture />,
    description: 'Time-bound efforts with specific goals and deadlines.',
  },
  resource: {
    icon: <Inventory2 />,
    description: 'Resources and reference material for quick lookup.',
  },
  speed_dial_item: {
    icon: <Speed />,
    description: 'Quick-navigation shortcuts displayed in the floating Speed Dial.',
  },
  status: {
    icon: <MdTraffic size={20} />,
    description: 'Stage in a workflow indicating progress toward completion.',
  },
  subcategory: {
    icon: <MdSubdirectoryArrowRight size={20} />,
    description: 'Second-level note organisation within categories.',
  },
  tag: {
    icon: <TagIcon />,
    description: 'Free-form labels applied to notes.',
  },
  weight: {
    icon: <Scale />,
    description: 'Personal weight tracking with multiple units.',
  },
};

/** All registered entity keys */
export type EntityKey = keyof typeof ENTITY_REGISTRY_DATA;

/** Array of all entity keys (sorted alphabetically) */
export const ENTITY_KEYS = Object.keys(ENTITY_REGISTRY_DATA).sort() as EntityKey[];

/**
 * Gets full entity configuration by key, deriving dataSource, cache, and paramName.
 */
export function getEntityConfig(key: EntityKey): EntityConfig {
  return {
    dataSource: getDataSource(key),
    cache: getCache(key),
    ...ENTITY_REGISTRY_DATA[key],
    paramName: toParamName(key),
  };
}

/** Proxy-based registry that computes full config on access */
export const ENTITY_REGISTRY = new Proxy({} as Record<string, EntityConfig>, {
  get(_, prop: string) {
    if (prop in ENTITY_REGISTRY_DATA) {
      return getEntityConfig(prop as EntityKey);
    }
    return undefined;
  },
}) as { readonly [K in EntityKey]: EntityConfig };
